import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { Asset } from "@shared/types/content-api";
import { assetKindLabel, pickerThumbnail } from "./CataloguePickerDrawer";
import { tokens } from "../../theme";

/**
 * The picks on a curated carousel, in the order a television draws them.
 *
 * ## Why this exists
 *
 * 🚨 `Carousel.assetIds` has always been ordered — *"array position IS the
 * display order, never a `position` field"* — but until this component there
 * was no way to CHANGE that order. The picker appends in click order, and the
 * only way to move a title was to deselect everything after it and pick it all
 * again. An operator could see the order in the preview and could not act on
 * it, which is the worst arrangement of the two.
 *
 * ## It shows the order; it does not SET it
 *
 * 🚨 Reordering moved to the preview, ruled by Sigge: *"have the user drag and
 * drop the preview instead, that's more natural"*. He is right — the preview
 * is the rail as a guest meets it, and arranging a row while looking at the row
 * needs no explaining at all.
 *
 * So this list keeps the two jobs the preview cannot do: it names every pick
 * INCLUDING the ones past the display limit, and it removes one. The numbers
 * stay because they are how the two surfaces refer to the same thing — drag
 * position 4 in the preview, watch row 4 move here.
 *
 * ## A row with no row
 *
 * A title can be here and not be resolvable: withdrawn from the catalogue,
 * still on the rail. It renders with its id and a note rather than vanishing —
 * ids in a selection are deliberately NOT validated on write (an operator
 * legitimately holds an id whose row is still only in the portal), so silently
 * dropping one here would delete a pick that the server would have kept. The
 * same row is what an id past the title-lookup cap draws.
 */

export interface PickedTitlesListProps {
  /** Authored order. Position IS display order. */
  value: string[];
  /** Resolved rows, keyed by assetId. Absent ids draw as unresolved. */
  byId: Map<string, Asset>;
  /** Ids never looked up — drawn as unresolved, but for a different reason. */
  unresolved?: string[];
  onChange: (next: string[]) => void;
}

export default function PickedTitlesList({
  value,
  byId,
  unresolved = [],
  onChange,
}: PickedTitlesListProps) {
  if (value.length === 0) return null;
  const neverLooked = new Set(unresolved);

  return (
    <Stack spacing={0.5} data-testid="picked-titles">
      {value.map((id, index) => (
        <TitleRow
          key={id}
          id={id}
          index={index}
          row={byId.get(id)}
          beyondCap={neverLooked.has(id)}
          onRemove={() => onChange(value.filter((other) => other !== id))}
        />
      ))}
    </Stack>
  );
}

interface RowProps {
  id: string;
  index: number;
  row?: Asset;
  beyondCap: boolean;
  onRemove: () => void;
}

function TitleRow({ id, index, row, beyondCap, onRemove }: RowProps) {
  // The same resolver the picker uses, so a channel that showed its logo while
  // being picked does not become a white rectangle once it is in the list.
  const thumb = row ? pickerThumbnail(row) : null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      data-testid={`picked-title-${index}`}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: tokens.divider,
        bgcolor: tokens.surface,
      }}
    >
      {/* The position, stated. The preview shows the same order horizontally
          and is where it is CHANGED; a number is what lets one refer to the
          other — "move the fourth one" is checkable in both places. */}
      <Typography
        sx={{
          width: 20,
          textAlign: "right",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          color: tokens.inkFaint,
        }}
      >
        {index + 1}
      </Typography>

      <Box
        sx={{
          width: 48,
          height: 29,
          borderRadius: 0.75,
          overflow: "hidden",
          flexShrink: 0,
          background: tokens.rowAlt,
        }}
      >
        {thumb && (
          <Box
            component="img"
            src={thumb}
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: row?.type === "channel" ? "contain" : "cover",
            }}
          />
        )}
      </Box>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600 }}
          noWrap
          title={row?.originalTitle ?? id}
        >
          {row?.originalTitle || id}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: tokens.inkFaint }} noWrap>
          {row
            ? `${assetKindLabel(row)}${
                row.productionYear ? ` · ${row.productionYear}` : ""
              }`
            : beyondCap
              ? "Not looked up — still ordered and removable"
              : "No longer in the catalogue"}
        </Typography>
      </Stack>

      <Tooltip title="Remove from this carousel">
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label={`Remove ${row?.originalTitle ?? id}`}
        >
          <CloseIcon fontSize="small" sx={{ color: tokens.inkFaint }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
